import { HTMLAttributes } from 'react'
import Link from 'next/link'

import Button from '@/components/base/button'
import Container from '@/components/base/container'
import Grid from '@/components/base/grid'
import Media from '@/components/base/media'
import Section from '@/components/base/section'
import Typography from '@/components/base/typography'

import { useHomeDrMotion } from './motion'
import Spacer from './spacer'
import { cn } from '@/lib/utils'
import { WPImage, WPLink, WPMedia } from '@/types'

export type HomeDrProps = HTMLAttributes<HTMLElement> &
  Partial<{
    intro: {
      title: string
      text: string
    }
    paragraph: {
      title: string
      text: string
      link: WPLink
    }
    profile: {
      image_title: string
      image: WPImage
    }
    media: WPMedia
  }>

const HomeDr = ({ intro, profile, paragraph, media, className, ...props }: HomeDrProps) => {
  const { motionRef } = useHomeDrMotion()

  return (
    <Section ref={motionRef} className={cn('z-10', className)} {...props}>
      <div className="grid">
        <div className="relative col-[1/-1] row-[1/-1]">
          {(media?.poster?.url || media?.video?.url) && (
            <div className="-z-1 pointer-events-none absolute inset-0">
              {/* <div className="absolute inset-0 z-10 bg-[black]/20" /> */}
              <Media media={media} />
            </div>
          )}
        </div>

        <div className="relative col-[1/-1] row-[1/-1]">
          <Spacer variant="2xl" className="bg-transparent dark:bg-transparent" />

          <Container>
            <Grid data-motion-row>
              <div
                className={cn(
                  'col-span-2 md:col-span-3 lg:col-span-4 lg:col-start-3 xl:col-span-3 xl:col-start-3',
                  'grid gap-y-spacing-lg md:gap-y-spacing-2xl'
                )}
              >
                {intro?.title ? (
                  <div className="flex">
                    <Typography
                      variant="h2"
                      className="text-text-primary-light"
                      data-motion-intro-title
                    >
                      {intro.title}
                    </Typography>
                  </div>
                ) : null}

                {intro?.text ? (
                  <div className="flex">
                    <Typography
                      variant="body"
                      className="text-text-primary-light"
                      data-motion-intro-paragraph
                    >
                      {intro.text}
                    </Typography>
                  </div>
                ) : null}
              </div>
            </Grid>

            <Spacer variant="sm" className="bg-transparent dark:bg-transparent" />

            <Grid data-motion-row>
              <div
                className={cn(
                  'col-span-2 md:col-span-3 md:col-start-4 lg:col-span-4 lg:col-start-9',
                  'grid gap-y-spacing-lg'
                )}
              >
                {profile?.image_title ? (
                  <div className="flex lg:justify-end">
                    <Typography
                      variant="large"
                      className="text-text-primary-light [&_b]:font-medium"
                      data-motion-profile-title
                    >{`<b>${profile.image_title}</b>`}</Typography>
                  </div>
                ) : null}

                {profile?.image ? (
                  <div className="flex">
                    <img
                      src={profile.image.url}
                      alt={profile.image.alt || 'Profilo'}
                      width={profile.image.width}
                      height={profile.image.height}
                      className="rounded-radius-minimal"
                    />
                  </div>
                ) : null}
              </div>
            </Grid>

            <Spacer variant="2xl" className="bg-transparent dark:bg-transparent" />

            <Grid data-motion-row>
              <div
                className={cn(
                  'col-span-2 md:col-span-4 md:col-start-3 lg:col-span-5 lg:col-start-7',
                  'grid gap-y-spacing-sm md:gap-y-spacing-lg'
                )}
              >
                {paragraph?.title ? (
                  <div className="flex">
                    <Typography
                      variant="large"
                      className="text-text-primary-light [&_b]:font-medium"
                      data-motion-text-title
                    >{`<b>${paragraph.title}</b>`}</Typography>
                  </div>
                ) : null}

                {paragraph?.text ? (
                  <div className="flex">
                    <Typography
                      variant="body"
                      className="text-text-primary-light"
                      data-motion-text-paragraph
                    >
                      {paragraph.text}
                    </Typography>
                  </div>
                ) : null}

                {paragraph?.link?.url ? (
                  <div className="md:flex">
                    <Button asChild className="self-start text-white-light" data-motion-text-button>
                      <Link href={paragraph.link.url} target={paragraph.link.target} scroll={false}>
                        <Typography variant="body" mask>
                          {paragraph.link.title}
                        </Typography>
                      </Link>
                    </Button>
                  </div>
                ) : null}
              </div>
            </Grid>
          </Container>

          <Spacer variant="2xl" className="bg-transparent dark:bg-transparent" />
        </div>
      </div>
    </Section>
  )
}

export default HomeDr
